import React from 'react'
import { Dashboard as DashboardIcon, DragIndicator as DragIndicatorIcon } from '@mui/icons-material'
import { NavLink as ReactRouterNavLink } from 'react-router-dom'
import { Typography, ListItemButton, ListItemSecondaryAction, ListItemIcon, ListItemText } from '@mui/material'
import { useActiveDashboardIdStore } from '@edge/spark/io-dashboards'
import { primaryColor, primaryColorOpacity } from '@edge/spark/ui-mui'

interface DashboardLinkProps {
    name: string
    isEditingLayouts: boolean
    onClick: () => void
    id: string
    expanded: boolean
}

export const DashboardLink: React.VFC<DashboardLinkProps> = ({
    name,
    isEditingLayouts,
    onClick,
    id,
    expanded,
}: DashboardLinkProps) => {
    /**
     * [Zustand Memoized Selector](https://docs.pmnd.rs/zustand/recipes#memoizing-selectors)
     * 1. Subscribe to `activeDashboardId`
     * 2. Only re-calculate if `id` prop changes, not every re-render
     * 3. Only trigger re-render if return changes between true/false */
    const isActiveDashboard = useActiveDashboardIdStore(React.useCallback((state) => state.id === id, [id]))

    return (
        <ReactRouterNavLink
            style={{ color: 'grey', textDecoration: 'none', borderRadius: '0px 50px 50px 0px' }}
            activeStyle={{ color: primaryColor, backgroundColor: primaryColorOpacity }}
            to="/"
            isActive={(match) => !!match?.isExact && isActiveDashboard}
        >
            <ListItemButton
                onClick={onClick}
                sx={{
                    minHeight: 48,
                    color: 'inherit',
                }}
            >
                <ListItemIcon
                    sx={{
                        color: 'inherit',
                        minWidth: 0,
                        ml: expanded ? 0.5 : 0.5,
                        mr: expanded ? 3 : 0,
                        justifyContent: 'center',
                    }}
                >
                    <DashboardIcon />
                </ListItemIcon>
                <ListItemText
                    sx={{ display: expanded ? 'block' : 'none' }}
                    primary={
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'inherit',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {name}
                        </Typography>
                    }
                />
                <ListItemSecondaryAction>
                    <DragIndicatorIcon
                        sx={{
                            display: isEditingLayouts ? 'none' : 'inline-block',
                            color: '#bbb',
                            paddingLeft: '28px',
                            opacity: expanded ? 1 : 0,
                        }}
                    />
                </ListItemSecondaryAction>
            </ListItemButton>
        </ReactRouterNavLink>
    )
}

export default DashboardLink
